<template>
  <HeaderBar
    class="hide-for-medium"
    :modifiers="['dark', 'sticky']"
  >
    <router-link
      :aria-label="$t('general.back')"
      class="header-bar__title typo-nav-item"
      :to="{ name: 'inspections', params: { scope: 'inspections' } }"
    >
      <UseSvg
        class="icon"
        name="arrow_left"
      />

      <div class="typo-small-title">
        {{ $t('nav.inspections') }}
      </div>
    </router-link>
  </HeaderBar>

  <InspectionNav
    v-if="currentOperation"
    bulkImport
    :currentOperation="currentOperation"
    :filterByOptions="filterByOptions"
    :inspection="inspection"
    :issues="issues"
    :loading="loading"
    :newIssueRoute="newIssueRoute"
    @delete-inspection="deleteInspection"
    @filter-by="updateFilter"
    @toggle-upload-modal="uploadModalIsOpen = !uploadModalIsOpen"
    @update-inspection="saveInspection"
    @update-status="updateStatus"
  />

  <!-- Issues List  -->
  <div
    v-if="!loading"
    class="scroll-container"
  >
    <div class="issue-list">
      <ul class="issue-list__issues">
        <IssueCard
          v-for="issue in issues"
          :key="issue.id"
          :issue="issue"
          :modifiers="['responsive']"
          :tags="true"
        />
      </ul>
    </div>
    <EmptyMessage
      v-if="issues.length === 0"
      :message="$t(`empty_states.issues.no_issues.${scope}`)"
      :title="$t('empty_states.issues.title')"
    >
      <div class="empty-message__image">
        <CircleSquareTarget />
      </div>
    </EmptyMessage>
  </div>

  <Loader v-if="loading" />

  <BulkUploadModal
    v-if="uploadModalIsOpen"
    @dismiss-modal="uploadModalIsOpen = !uploadModalIsOpen"
  />
  <router-view v-if="['bulkImport'].includes($route.name)" />
</template>

<script setup>
import { watch, ref, onMounted, computed, reactive, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import useAjax from '@/functions/use_ajax';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import IssueCard from '@/components/issues/issue_card.vue';
import BulkUploadModal from '@/components/generic/bulk/bulk_import_modal.vue';
import InspectionNav from '@/components/generic/inspection_nav.vue';

// Assets
import CircleSquareTarget from '@/images/circle-square-target.svg';

const emit = defineEmits(['statusChange', 'updateInspection', 'deleteInspection']);
const props = defineProps({
    organisationId: { type: String, required: true },
    operationId: { type: String, required: true },
    inspectionId: { type: String, required: true },
    scope: { type: String, default: 'inspections' },
  });

const ajax = useAjax();
const route = useRoute();
const router = useRouter();
const uploadModalIsOpen = ref(false);
const currentOperation = inject('currentOperation') || {};
const newIssueRoute = computed(() => { return { name: 'newIssue' }; });

const inspection = reactive({
  approved_by: null,
  date: null,
  inspected_by: null,
  inspection_type: null,
  policy: {},
  professional_engineer: null,
  status: null,
  download_url: null,
  issues: [],
  trusted_user: false
});
const issues = ref([]);
const loading = ref(true);
const filterBy = ref(null);
const filterByOptions = ref([]);

const inspectionURL = computed(() => {
  return `/organisations/${props.organisationId}/operations/${props.operationId}/plants/inspections/${props.inspectionId}`;
});
const approvalURL = computed(() => {
  return `/organisations/${props.organisationId}/operations/${props.operationId}/plants/inspections/${props.inspectionId}/approval`;
});

onMounted(() => {
  fetchInspection();
});

/**
 * Watches for changes on (:id) route param then,
 *  finds and returns the inspection by id
 */
watch(() => props.inspectionId, fetchInspection);

/**
 * Watches for changes on the filter by dropdown
 */
watch(filterBy, () => {
  loading.value = true;
  if (!filterBy.value) {
    issues.value = inspection.issues;
  } else {
    issues.value = inspection.issues.filter((item) => item.status === filterBy.value);
  }
  loading.value = false;
});

/**
 * Resets filter label text
 */
watch(() => route.params.inspectionId, () => filterBy.value = null);

/**
 * Fetches the inspection details from the server
 */
async function fetchInspection() {
  if (props.inspectionId) {
    loading.value = true;
    const { ok, data } = await ajax(inspectionURL.value);
    if (ok) {
      Object.assign(inspection, data);
      issues.value = data.issues;
      filterByOptions.value = generateFilterList(issues.value, 'status');
    }
    loading.value = false;
  }
}

/**
 * Generates a list of all unique statuses
 * available in the current inspection's issue list
 */
function generateFilterList(data, key) {
  /**
   * Creates a list of all unique key items (e.g status)
   * that exist in the provided data set
   */
  const mapData = data.reduce((p,c) => p.has(c[key]) ? p.set(c[key]) : p.set(c[key]), new Map());
  /**
   * Maps the unique list and formats a new
   * array with other relevant data
   */
  const result = [...mapData].map(e => {
    const count = data.filter(obj => obj[key] === e[0]).length;
    const status = `${e[0] !== 'resolved' ? e[0] : e[0] + '_issue'}`;
    const labelText = count <= 1 ? I18n.t(`inspections.issues.filter.${status}`) : I18n.t(`inspections.issues.filter.${status}s`);
    return e[0] && ({value: e[0], label: labelText, total: count});
  });
  result[0] && result.unshift({value: null, label: I18n.t('inspections.issues.filter.all_issues'), total: data.length});
  return result[0] && result || [];
}

/**
 * Updates the inspection status
 *  and emits 'statusChange' to parent to handle update
 */
async function updateStatus(newStatus) {
  inspection.status = newStatus;

  let { ok, data } = await ajax(approvalURL.value, {
    method: 'PATCH',
    body: { inspection: { status: newStatus } },
  });

  if (ok) {
    Object.assign(inspection, data);
    emit('statusChange', data);
  }
}

/**
 * Save inspection
 */
async function saveInspection(data) {
  Object.assign(inspection, data);
  emit('updateInspection', data);
}

async function deleteInspection() {
  let { ok } = await ajax(
    `/organisations/${props.organisationId}/operations/${props.operationId}/plants/inspections/${props.inspectionId}`,
    { method: 'DELETE' }
  );

  if (ok) {
    emit('deleteInspection');
    router.replace({ name: 'inspections', params: { scope: 'inspections' } });
  }
}

function updateFilter(value) {
  filterBy.value = value;
}
</script>
